import React from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lock, LayoutGrid, PiggyBank, Award, RefreshCcw, ShieldCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BentoItem } from "@/components/ui/cybernetic-bento-grid";

const valueBlocks = [{
  icon: Lock,
  title: "Your brand voice, locked in from Day 1",
  description: <>Brand Vaults capture your voice, visuals, strategy, and rules. Every piece of content across your entire team feels unmistakably YOU.<br /><br />No more "this doesn't sound like us" revisions.</>,
  gradient: "from-purple-500 to-primary"
}, {
  icon: LayoutGrid,
  title: "Create everything, in one place",
  description: <>Research, ideation, copy, visuals, video, editing and publishing, all in one workspace that understands your brand.<br /><br />Stop bouncing between tools. Start shipping faster.</>,
  gradient: "from-purple-500 to-primary"
}, {
  icon: PiggyBank,
  title: "Replace 3-5 tools with one system",
  description: <>One scalable subscription. One login. One source of truth.<br /><br />Save budget, save sanity, and scale without adding complexity.</>,
  gradient: "from-green-500 to-emerald-500"
}, {
  icon: Award,
  title: "Raise the bar without burning out",
  description: <>Upskill your team with AI that's thoughtful, strategic, and always on-brand.<br /><br />Deliver higher-quality work without working longer hours.</>,
  gradient: "from-blue-500 to-cyan-500"
}, {
  icon: RefreshCcw,
  title: "Stay cutting-edge without lifting a finger",
  description: <>Your AI stack updates automatically. New models, features, and integrations drop seamlessly.<br /><br />No patches, no retraining, no tech babysitting.</>,
  gradient: "from-orange-500 to-amber-500"
}, {
  icon: ShieldCheck,
  title: "Enterprise-ready, built YOUR way",
  description: <>Dedicated workspaces, custom permissions, SOC2 and GDPR-grade security, with optional integrations and expert support.<br /><br />Make gimmefy fit your exact workflow.</>,
  gradient: "from-slate-500 to-blue-500"
}];

const SkillsSection = () => {
  const navigate = useNavigate();
  return (
    <section id="why-gimmefy" className="w-full py-16 sm:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-16"
        >
          <Badge variant="outline" className="mb-4">Why gimmefy?</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Built for <span className="bg-gradient-primary bg-clip-text text-transparent">marketers</span>. Powered by <span className="bg-gradient-primary bg-clip-text text-transparent">AI</span>.
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            You turn everyday chaos into marketing magic. Fusion transforms that chaos into a powerful system, combining your vision and expertise with tireless AI support.
          </p>
        </motion.div>

        {/* Bento Grid with staggered animation */}
        <div className="bento-grid mb-12 lg:mb-16">
          {valueBlocks.map((block, index) => {
            const Icon = block.icon;
            return (
              <motion.div
                key={block.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full"
              >
                <BentoItem className="group hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                  <motion.div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${block.gradient} flex items-center justify-center mb-4`}
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: index * 0.2 }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3">
                    {block.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {block.description}
                  </p>
                </BentoItem>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={() => navigate("/comparison")}
            size="lg"
            className="rounded-full px-8 py-6 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300"
          >
            See Fusion in Action
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default SkillsSection;